import { useQuery } from '@tanstack/react-query';
import { Plus, FileText, CalendarDays, ListTodo, AlertTriangle, BarChart3, Mail, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard } from '@/components/shared/StatCard';
import { TaskCard } from '@/features/tasks/components/TaskCard';
import { MorningBrief } from '@/features/dashboard/MorningBrief';
import { QuickNote } from '@/features/dashboard/QuickNote';
import { supabase } from '@/lib/supabase';

export function MyDayPage() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const { data: overdueTasks, isLoading: loadingOverdue } = useQuery({
    queryKey: ['tasks', 'overdue'],
    queryFn: async () => {
      const { data } = await supabase
        .from('task')
        .select('id, title, status, priority, due_date')
        .lt('due_date', `${today}T00:00:00`)
        .in('status', ['todo', 'in_progress'])
        .order('due_date')
        .limit(5);
      return data ?? [];
    },
  });

  const { data: todayTasks, isLoading: loadingToday } = useQuery({
    queryKey: ['tasks', 'today'],
    queryFn: async () => {
      const { data } = await supabase
        .from('task')
        .select('id, title, status, priority, due_date')
        .gte('due_date', `${today}T00:00:00`)
        .lte('due_date', `${today}T23:59:59`)
        .order('priority', { ascending: false })
        .limit(10);
      return data ?? [];
    },
  });

  const { data: calendarEvents, isLoading: loadingCalendar } = useQuery({
    queryKey: ['calendar', 'today'],
    queryFn: async () => {
      const start = `${today}T00:00:00Z`;
      const end = `${today}T23:59:59Z`;
      const { data } = await supabase.functions.invoke('calendar-events', {
        body: { start_date: start, end_date: end },
      });
      return (data?.events ?? []) as Array<{ id: string; title: string; start: string; end: string; provider: string }>;
    },
  });

  const { data: unreadCount } = useQuery({
    queryKey: ['email', 'unread-count'],
    queryFn: async () => {
      const { data } = await supabase.functions.invoke('email-inbox', { body: { folder: 'inbox', limit: 1 } });
      return (data?.unread_count ?? 0) as number;
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['tasks', 'stats'],
    queryFn: async () => {
      const { count: total } = await supabase
        .from('task').select('*', { count: 'exact', head: true });
      const { count: done } = await supabase
        .from('task').select('*', { count: 'exact', head: true }).eq('status', 'done');
      const { count: overdue } = await supabase
        .from('task').select('*', { count: 'exact', head: true })
        .lt('due_date', `${today}T00:00:00`).in('status', ['todo', 'in_progress']);
      return { total: total ?? 0, done: done ?? 0, overdue: overdue ?? 0 };
    },
  });

  return (
    <div>
      <PageHeader title="My Day" description={new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} />

      {/* Stats row */}
      <div className="mb-6 grid gap-4 md:grid-cols-3">
        <StatCard title="Total Tasks" value={stats?.total ?? 0} icon={ListTodo} />
        <StatCard title="Completed" value={stats?.done ?? 0} icon={BarChart3} />
        <StatCard title="Overdue" value={stats?.overdue ?? 0} icon={AlertTriangle} />
      </div>

      {/* Main 3-column layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: Schedule + Overdue + Today */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" />
                Email
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="ghost" className="w-full justify-start gap-2 text-sm" onClick={() => navigate('/email')}>
                <Mail className="h-4 w-4" />
                {unreadCount ? `${unreadCount} unread messages` : 'Inbox up to date'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarDays className="h-4 w-4" />
                Schedule ({calendarEvents?.length ?? 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingCalendar && <Skeleton className="h-10 w-full" />}
              {calendarEvents?.map((ev) => (
                <Button key={ev.id} variant="ghost" className="w-full justify-start gap-2 text-sm h-auto py-2" onClick={() => navigate('/calendar')}>
                  <Clock className="h-3 w-3 shrink-0" />
                  <span className="truncate">{ev.title}</span>
                  <span className="ml-auto text-xs text-muted-foreground shrink-0">
                    {new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </Button>
              ))}
              {!loadingCalendar && calendarEvents?.length === 0 && (
                <p className="text-sm text-muted-foreground">No meetings today</p>
              )}
            </CardContent>
          </Card>

          {/* Overdue */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Overdue ({overdueTasks?.length ?? 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingOverdue && <Skeleton className="h-16 w-full" />}
              {overdueTasks?.map((t) => (
                <TaskCard key={t.id} task={t} onClick={() => navigate(`/tasks/${t.id}`)} />
              ))}
              {!loadingOverdue && overdueTasks?.length === 0 && (
                <p className="text-sm text-muted-foreground">No overdue tasks</p>
              )}
            </CardContent>
          </Card>

          {/* Today */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Today's Tasks ({todayTasks?.length ?? 0})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingToday && <Skeleton className="h-16 w-full" />}
              {todayTasks?.map((t) => (
                <TaskCard key={t.id} task={t} onClick={() => navigate(`/tasks/${t.id}`)} />
              ))}
              {!loadingToday && todayTasks?.length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing on your plate today!</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Center: AI Brief */}
        <MorningBrief />

        {/* Right: Quick Actions + Quick Note */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/tasks')}>
                <Plus className="h-4 w-4" /> New Task
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/documents')}>
                <FileText className="h-4 w-4" /> New Document
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/email')}>
                <Mail className="h-4 w-4" /> Compose Email
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/calendar')}>
                <CalendarDays className="h-4 w-4" /> New Event
              </Button>
            </CardContent>
          </Card>
          <QuickNote />
        </div>
      </div>
    </div>
  );
}
